/**
 * Domain model.
 *
 * Three deliberate departures from the 2020 shape:
 *
 * 1. Dates are plain `Date`, not Firestore `Timestamp`. Conversion happens
 *    once, in `src/data/mappers.ts`, so nothing below this line knows Firestore
 *    exists.
 *
 * 2. Exits are a **list**, not a single `sell` leg. The UI creates exactly one
 *    and enforces that, so nothing about the current product is more complex —
 *    but scaling out of a position later becomes a UI feature rather than a
 *    data migration. `readonly [TradeLeg, ...TradeLeg[]]` is a non-empty tuple,
 *    so `exits[0]` is still statically guaranteed under `noUncheckedIndexedAccess`
 *    and "closed trade with no exit" remains unrepresentable.
 *
 * 3. `active` is derived, not stored. The 2020 document could hold
 *    `active: true` alongside a populated sell leg — two fields that could
 *    disagree about the same fact.
 */

export type TradeType = "LONG" | "SHORT";

/**
 * What kind of thing was traded — which decides whether a price can be looked
 * up at all.
 *
 * `crypto` resolves through DefiLlama by CoinGecko id. `stock` has no feed:
 * keyless equity sources do not send CORS headers, and the keyed ones put a
 * key in the bundle and — at any real number of users — exchange redistribution
 * fees on the bill. So a stock's price is whatever the owner last entered, and
 * every price path has to tolerate not having one.
 */
export type AssetType = "crypto" | "stock";

/** One side of a trade — the buy, or one exit from the position. */
export interface TradeLeg {
  date: Date;
  price: number;
  quantity: number;
  /** Price on `date`, in the user's preferred currency, captured at write time. */
  pricePreferredCurrency?: number;
  /**
   * Which currency `pricePreferredCurrency` is denominated in, e.g. "usd".
   *
   * Captured beside the figure because the figure is final — it cannot be
   * restated when the preference changes (historical non-USD rates are not
   * obtainable on the free tier). Without the code, changing the preference
   * silently reinterprets every figure written before it.
   *
   * Absent on documents written before this field existed. Those cannot be
   * disambiguated, so absent means "unknown", never "assume the current
   * preference".
   */
  pricePreferredCurrencyCode?: string;
}

export interface TradeTargets {
  /** Price at which the position should be closed for a gain. */
  profit: number;
  /** Price at which the position should be closed to cap a loss. */
  stopLoss: number;
}

interface TradeBase {
  id: string;
  /**
   * Defaults to `"crypto"` when absent, which is right for every document
   * written before this field existed — the app supported nothing else.
   */
  assetType: AssetType;
  /**
   * What was traded. A CoinGecko coin id for crypto (`"bitcoin"`), a ticker
   * symbol for stocks (`"AAPL"`).
   *
   * The name is inherited from the 2020 schema and is now a little wide of the
   * mark, but it is the field every stored document already uses; renaming it
   * would be a migration for a comment's worth of benefit.
   */
  coinBase: string;
  /** Quote currency, e.g. "USD" | "BTC" | "USDC". Always fiat for stocks. */
  coinTarget: string;
  /**
   * Last known price, for assets with no feed. Absent until the owner enters
   * one — absent means unknown, and the UI says so rather than showing a zero
   * or a stale figure without its date.
   */
  lastPrice?: number;
  /** When `lastPrice` was entered, so the UI can show how stale it is. */
  lastPriceAt?: Date;
  /**
   * CoinGecko exchange id. Optional — it drove nothing but a dropdown, and is
   * no longer collected. Retained so existing records still render.
   */
  exchange?: string;
  tradeType: TradeType;
  dateAdded: Date;
  updatedAt: Date;
  buy: TradeLeg;
  target: TradeTargets;
}

/** A position still held — no exits taken. */
export interface OpenTrade extends TradeBase {
  exits: readonly [];
}

/** A position with at least one exit. */
export interface ClosedTrade extends TradeBase {
  exits: readonly [TradeLeg, ...TradeLeg[]];
  /** Realised profit across all exits, in the trade's own quote currency. */
  profit?: number;
  /** Realised profit across all exits, in the user's preferred currency. */
  profitPreferredCurrency?: number;
  /** Which currency `profitPreferredCurrency` is in. See `pricePreferredCurrencyCode`. */
  profitPreferredCurrencyCode?: string;
}

export type Trade = OpenTrade | ClosedTrade;

/**
 * Whether the position has been exited.
 *
 * Today one exit closes a trade outright. When partial exits arrive this
 * becomes `remainingQuantity(trade) <= 0` and nothing else has to change.
 */
export function isClosed(trade: Trade): trade is ClosedTrade {
  return trade.exits.length > 0;
}

export function isOpen(trade: Trade): trade is OpenTrade {
  return trade.exits.length === 0;
}

/**
 * Whether a price can be looked up for this asset automatically.
 *
 * Every branch on "can we price this?" goes through here rather than comparing
 * `assetType` directly, so a third kind of manually-priced asset is one line
 * rather than a hunt through the codebase.
 */
export function hasPriceFeed(trade: Pick<Trade, "assetType">): boolean {
  return trade.assetType === "crypto";
}

/** How a trade turned out, relative to its targets. */
export type TradeOutcome = "open" | "hitProfitTarget" | "hitStopLoss" | "between";

export interface UserPreferences {
  /** Lowercase currency code, e.g. "usd". */
  preferredCurrency: string;
  dateAdded: Date;
}

export interface AppUser extends UserPreferences {
  id: string;
  authProvider: {
    displayName: string | null;
    email: string | null;
    providerId: string;
  };
}

/** A distinct base/quote pairing, used to deduplicate price lookups. */
export interface TradePair {
  coinBase: string;
  coinTarget: string;
}
